/**
 * Turn extraction for the conversation transcript.
 *
 * A run's `input` is rarely a bare user message — it's usually the whole prompt
 * the agent was called with: a system message, prior history, then the actual
 * new user turn. Rendering all of that as "what the user said" would repeat the
 * entire history on every run and bury the one line that is new.
 *
 * So for the chat view we take the *last* user message out of the input and the
 * assistant's reply out of the output. Everything else (system prompts, tool
 * chatter, the raw payloads) stays available in the run drawer, one click away.
 *
 * TWIN MODULE — KEEP IN SYNC
 *
 * backend/services/evals/conversationTurns.js is the same logic, in CommonJS,
 * feeding conversation-level evaluators. It cannot be a shared import: the
 * `shared/` alias here resolves to client/src/shared, and CRA's module scope
 * forbids reaching outside client/src.
 *
 * The two must agree, because the transcript rendered below is the text a
 * conversation evaluator scores — a customer disputing a score has to be
 * looking at what the judge read. The backend's
 * __tests__/conversationTurns.test.js pins the shared behaviour; change this
 * file and you change what those tests are asserting about.
 */

/** Parse a value that may be an object, a JSON string, or plain text. */
function parseMaybeJson(value) {
  if (value == null) return null;
  if (typeof value === 'object') return value;
  const s = String(value).trim();
  if (!s) return null;
  if (s[0] !== '{' && s[0] !== '[') return s;
  try {
    return JSON.parse(s);
  } catch {
    return s;
  }
}

function isMessageLike(m) {
  return m && typeof m === 'object' && !Array.isArray(m) && 'role' in m;
}

/** Find a chat-message array inside a payload, unwrapping one level of envelope. */
function asMessages(parsed) {
  if (Array.isArray(parsed) && parsed.length > 0 && parsed.every(isMessageLike)) return parsed;
  if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
    for (const k of ['messages', 'input', 'prompt', 'conversation', 'context', 'history']) {
      const inner = parsed[k];
      if (Array.isArray(inner) && inner.length > 0 && inner.every(isMessageLike)) return inner;
    }
  }
  return null;
}

/** Message content may be a string or an array of content blocks. */
function contentToText(content) {
  if (content == null) return '';
  if (typeof content === 'string') return content;
  if (Array.isArray(content)) {
    return content
      .map((part) => {
        if (typeof part === 'string') return part;
        if (part && typeof part === 'object') return part.text || part.content || '';
        return '';
      })
      .filter(Boolean)
      .join('\n');
  }
  if (typeof content === 'object') return content.text || '';
  return String(content);
}

/** Pull a single string out of a non-message payload, if there's an obvious one. */
function primaryText(parsed) {
  if (parsed == null) return null;
  if (typeof parsed === 'string') return parsed;
  if (typeof parsed !== 'object' || Array.isArray(parsed)) return null;
  for (const k of ['text', 'content', 'query', 'question', 'message', 'prompt', 'answer', 'response', 'result', 'output']) {
    const v = parsed[k];
    if (typeof v === 'string' && v.trim()) return v;
  }
  return null;
}

/**
 * The user side of a run.
 * Returns `{ text, value, truncatedHistory }` — `text` when we could isolate a
 * message, `value` as the fallback payload for RichValueView, and
 * `truncatedHistory` telling the UI how many earlier turns were folded away.
 */
export function extractUserTurn(input) {
  const parsed = parseMaybeJson(input);
  if (parsed == null) return null;

  const messages = asMessages(parsed);
  if (messages) {
    const userIdx = messages.map((m) => String(m.role).toLowerCase()).lastIndexOf('user');
    const picked = userIdx >= 0 ? messages[userIdx] : messages[messages.length - 1];
    const text = contentToText(picked?.content);
    return {
      text: text || null,
      value: text ? null : picked,
      role: picked?.role || 'user',
      // Count everything before the message we showed, so the UI can say what
      // it hid rather than silently dropping context.
      truncatedHistory: userIdx >= 0 ? userIdx : messages.length - 1,
    };
  }

  const text = primaryText(parsed);
  return { text: text || null, value: text ? null : parsed, role: 'user', truncatedHistory: 0 };
}

/** The agent side of a run. Same contract as extractUserTurn. */
export function extractAssistantTurn(output) {
  const parsed = parseMaybeJson(output);
  if (parsed == null) return null;

  const messages = asMessages(parsed);
  if (messages) {
    const idx = messages.map((m) => String(m.role).toLowerCase()).lastIndexOf('assistant');
    const picked = idx >= 0 ? messages[idx] : messages[messages.length - 1];
    const text = contentToText(picked?.content);
    return {
      text: text || null,
      value: text ? null : picked,
      role: picked?.role || 'assistant',
      truncatedHistory: 0,
    };
  }

  const text = primaryText(parsed);
  return { text: text || null, value: text ? null : parsed, role: 'assistant', truncatedHistory: 0 };
}

/** One-line preview for a conversation row in the list. */
export function turnPreview(payload, max = 120) {
  const turn = extractUserTurn(payload);
  if (!turn) return null;
  let s = turn.text;
  if (!s && turn.value != null) {
    try { s = JSON.stringify(turn.value); } catch { s = null; }
  }
  if (!s) return null;
  const flat = String(s).replace(/\s+/g, ' ').trim();
  return flat.length > max ? `${flat.slice(0, max)}…` : flat;
}
